import React, { useState, useEffect, useCallback } from 'react';
import { useCustomDialog } from '../../context/DialogContext';
import SuperAdminLayout from '../SuperAdminLayout';

const ManageSaasCoupons = () => {
    const { alert: customAlert } = useCustomDialog();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCoupon, setNewCoupon] = useState({
        code: '',
        discount_type: 'percent',
        discount_value: '',
        applicable_plan_name: 'ALL',
        applicable_cycle: 'ALL',
        affiliate_name: '',
        max_uses: 100
    });
    
    const [saasPlans, setSaasPlans] = useState([]);

    // For viewing usage details
    const [viewingCoupon, setViewingCoupon] = useState(null);
    const [usageDetails, setUsageDetails] = useState([]);
    const [loadingUsage, setLoadingUsage] = useState(false);
    const token = localStorage.getItem('saas_superadmin_token');

    const fetchCoupons = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_admin_saas_coupons.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setCoupons(data.coupons);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchCoupons();
        fetchPlans();
    }, [fetchCoupons]);

    const fetchPlans = async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_public_plans.php');
            const data = await res.json();
            if (data.success && data.plans) {
                setSaasPlans(data.plans);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_admin_saas_coupons.php', {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newCoupon)
            });
            const data = await res.json();
            if (data.success) {
                customAlert('Coupon created successfully.', 'Success', 'success');
                setNewCoupon({ code: '', discount_type: 'percent', discount_value: '', applicable_plan_name: 'ALL', applicable_cycle: 'ALL', affiliate_name: '', max_uses: 100 });
                fetchCoupons();
            } else {
                customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            customAlert('Failed to create coupon.', 'Error', 'danger');
        }
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_admin_saas_coupons.php', {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id, is_active: currentStatus === 1 ? 0 : 1 })
            });
            const data = await res.json();
            if (data.success) {
                fetchCoupons();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const viewUsage = async (code) => {
        setViewingCoupon(code);
        setLoadingUsage(true);
        try {
            const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_admin_saas_coupons.php?coupon_code=${code}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setUsageDetails(data.usage);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingUsage(false);
        }
    };

    const getStatusClass = (isActive) => {
        return isActive === 1 ? 'status-active' : 'status-expired';
    };

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <h2 className="sa-page-title">Manage SaaS Coupons & Influencers</h2>
                    <p className="sa-page-subtitle" style={{ margin: 0 }}>Create promotional codes, track affiliate performance, and manage SaaS discounts.</p>
                </div>
            
                <div className="sa-panel" style={{ marginBottom: '2rem' }}>
                    <h3 className="sa-panel-title"><i className="fas fa-ticket-alt"></i> Create New Coupon</h3>
                    <form onSubmit={handleCreate}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Coupon Code (e.g. YOUTUBE50)</label>
                                <input type="text" className="sa-input" style={{ width: '100%' }} value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} required />
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Affiliate/Influencer Name</label>
                                <input type="text" className="sa-input" style={{ width: '100%' }} value={newCoupon.affiliate_name} onChange={e => setNewCoupon({...newCoupon, affiliate_name: e.target.value})} placeholder="Optional tracking name" />
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Discount Type</label>
                                <select className="sa-select" style={{ width: '100%' }} value={newCoupon.discount_type} onChange={e => setNewCoupon({...newCoupon, discount_type: e.target.value})}>
                                    <option value="percent">Percentage (%)</option>
                                    <option value="flat">Flat Amount (₹)</option>
                                </select>
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Discount Value</label>
                                <input type="number" className="sa-input" style={{ width: '100%' }} value={newCoupon.discount_value} onChange={e => setNewCoupon({...newCoupon, discount_value: e.target.value})} required />
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Applicable Plan</label>
                                <select className="sa-select" style={{ width: '100%' }} value={newCoupon.applicable_plan_name} onChange={e => setNewCoupon({...newCoupon, applicable_plan_name: e.target.value})}>
                                    <option value="ALL">Any Plan</option>
                                    {saasPlans.map(p => (
                                        <option key={p.id} value={p.plan_name}>{p.plan_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Applicable Cycle</label>
                                <select className="sa-select" style={{ width: '100%' }} value={newCoupon.applicable_cycle} onChange={e => setNewCoupon({...newCoupon, applicable_cycle: e.target.value})}>
                                    <option value="ALL">Any Billing Cycle (Monthly & Yearly)</option>
                                    <option value="Monthly">Monthly Only</option>
                                    <option value="Yearly">Yearly Only</option>
                                </select>
                            </div>
                            <div className="sa-form-group">
                                <label className="sa-form-label">Max Uses (0 = unlimited)</label>
                                <input type="number" className="sa-input" style={{ width: '100%' }} value={newCoupon.max_uses} onChange={e => setNewCoupon({...newCoupon, max_uses: e.target.value})} required />
                            </div>
                        </div>
                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="submit" className="sa-primary-btn" style={{ minWidth: '200px' }}>
                                <i className="fas fa-plus"></i> Create Coupon
                            </button>
                        </div>
                    </form>
                </div>

                <div className="sa-action-bar" style={{ marginBottom: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)' }}>Active Coupons List</h3>
                    <button onClick={() => fetchCoupons()} className="sa-secondary-btn"><i className="fas fa-sync-alt"></i> Refresh</button>
                </div>

                <div className="sa-table-container">
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Affiliate Name</th>
                                <th>Discount</th>
                                <th>Restrictions</th>
                                <th>Usage Count</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}><i className="fas fa-circle-notch fa-spin fa-2x"></i></td></tr>
                            ) : coupons.length === 0 ? (
                                <tr><td colSpan="6" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No coupons found. Create one above.</td></tr>
                            ) : coupons.map(c => (
                                <tr key={c.id}>
                                    <td><strong style={{ color: 'var(--primary-orange)' }}>{c.code}</strong></td>
                                    <td>{c.affiliate_name || <span style={{ color: 'var(--text-secondary)' }}>-</span>}</td>
                                    <td>
                                        <span style={{ background: 'var(--bg-dark)', padding: '4px 8px', borderRadius: '4px', fontWeight: 600 }}>
                                            {c.discount_type === 'percent' ? `${c.discount_value}% OFF` : `₹${c.discount_value} OFF`}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: '0.85rem' }}>Plan: <strong>{c.applicable_plan_name || 'ALL'}</strong></div>
                                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Cycle: <strong>{c.applicable_cycle || 'ALL'}</strong></div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ flex: 1, background: 'var(--border-color)', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', background: 'var(--primary-orange)', width: c.max_uses > 0 ? `${Math.min(100, (c.used_count / c.max_uses) * 100)}%` : '100%' }}></div>
                                            </div>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{c.used_count} / {c.max_uses === 0 ? '∞' : c.max_uses}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusClass(c.is_active)}`}>
                                            {c.is_active === 1 ? 'Active' : 'Disabled'}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                            <button className="sa-action-btn" title="View Usage" onClick={() => viewUsage(c.code)}>
                                                <i className="fas fa-chart-bar" style={{ color: 'var(--info)' }}></i>
                                            </button>
                                            {c.is_active === 1 ? (
                                                <button className="sa-action-btn danger" title="Disable" onClick={() => toggleStatus(c.id, c.is_active)}>
                                                    <i className="fas fa-ban"></i>
                                                </button>
                                            ) : (
                                                <button className="sa-action-btn success" title="Enable" onClick={() => toggleStatus(c.id, c.is_active)}>
                                                    <i className="fas fa-check-circle"></i>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* USAGE MODAL */}
                {viewingCoupon && (
                    <div className="sa-modal-overlay">
                        <div className="sa-modal" style={{ maxWidth: '800px', width: '90%' }}>
                            <div className="sa-modal-header">
                                <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--text-primary)' }}>Usage Details: <span style={{ color: 'var(--primary-orange)' }}>{viewingCoupon}</span></h2>
                                <button onClick={() => setViewingCoupon(null)} className="sa-modal-close"><i className="fas fa-times"></i></button>
                            </div>
                            
                            <div style={{ marginTop: '1rem', maxHeight: '60vh', overflowY: 'auto' }}>
                                {loadingUsage ? (
                                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}><i className="fas fa-circle-notch fa-spin fa-2x"></i></div>
                                ) : usageDetails.length === 0 ? (
                                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>This coupon has not been used yet.</div>
                                ) : (
                                    <div className="sa-table-container" style={{ margin: 0, boxShadow: 'none', border: '1px solid var(--border-color)' }}>
                                        <table className="sa-table">
                                            <thead>
                                                <tr>
                                                    <th>Restaurant</th>
                                                    <th>Contact</th>
                                                    <th>Final Amount Paid</th>
                                                    <th>Date Used</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {usageDetails.map((u, i) => (
                                                    <tr key={i}>
                                                        <td><strong>{u.restaurant_name}</strong></td>
                                                        <td>
                                                            <div style={{ fontSize: '0.85rem' }}>{u.email}</div>
                                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{u.owner_phone}</div>
                                                        </td>
                                                        <td><strong style={{ color: 'var(--success)' }}>₹{u.amount}</strong></td>
                                                        <td>{new Date(u.created_at).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default ManageSaasCoupons;
